import { useState } from 'react';
import { Users, Plus, ArrowDownAZ, Clock } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ContactCard } from '../../components/ContactCard';
import { AppSelect } from '../../components/AppSelect';
import { Modal } from '../../components/Modal';
import { useData } from '../../context/DataContext';
import { useDeleteConfirm } from '../../hooks/useDeleteConfirm';
import { Contact } from '../../types';
import { useLanguage } from '../../functions/useLanguage';


interface ContactModalProps {
  onClose: () => void;
  onSave: (contact: Omit<Contact, 'id'>) => void;
  initial?: Contact;
}

function ContactModal({ onClose, onSave, initial }: ContactModalProps) {
  const { t } = useLanguage();

  const normalizeRole = (value?: Contact['role']): Exclude<Contact['role'], 'Nephrologist'> => {
    if (!value || value === 'Nephrologist') return 'Vet';
    return value;
  };

  const [name, setName] = useState(initial?.name ?? '');
  const [role, setRole] = useState<Exclude<Contact['role'], 'Nephrologist'>>(normalizeRole(initial?.role));
  const [phone, setPhone] = useState(initial?.phone_number ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [errors, setErrors] = useState<{ name?: boolean; phone?: boolean }>({});
  const isEditing = !!initial;

  const roleOptions = [
    { value: 'Vet', label: t('contacts.nephrologist') },
    { value: 'Pharmacy', label: t('contacts.pharmacy') },
    { value: 'Emergency', label: t('contacts.emergency') },
    { value: 'Other', label: t('contacts.other') },
  ] as const;

  const handleSubmit = () => {
    const newErrors = { name: !name.trim(), phone: !phone.trim() };
    setErrors(newErrors);
    if (newErrors.name || newErrors.phone) return;
    onSave({ user_id: 'current-user', name: name.trim(), role, phone_number: phone.trim(), email: email.trim() || undefined });
  };

  const inputClass = cn('w-full p-3 border rounded-xl bg-white border-gray-300 text-text-primary');

  return (
    <Modal
      open
      title={isEditing ? t('contacts.editContact') : t('contacts.addContact')}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className={cn('flex-1 p-3 rounded-xl font-semibold border transition-colors border-gray-200 text-text-secondary hover:bg-gray-50')}
          >
            {t('contacts.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 p-3 bg-primary-brown text-white rounded-xl font-bold hover:bg-hover-dark transition-colors"
          >
            {isEditing ? t('contacts.saveChanges') : t('contacts.saveContact')}
          </button>
        </>
      }
    >
      <input
        required
        placeholder={t('contacts.name')}
        value={name}
        onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: false })); }}
        className={cn(inputClass, errors.name && 'border-error-red\/40 bg-error-red\/5')}
      />
      {errors.name && <p className="text-xs text-error-red -mt-2">{t('contacts.nameRequired')}</p>}
      <AppSelect
        value={role}
        onChange={value => setRole(value as Exclude<Contact['role'], 'Nephrologist'>)}
        options={roleOptions.map(opt => ({ value: opt.value, label: opt.label }))}
        fullWidth
        className={cn(
          'w-full p-3 border rounded-xl bg-white border-gray-300 text-text-primary',
          'focus:ring-2 focus:ring-primary-brown/30'
        )}
      />
      <input
        required
        type="tel"
        placeholder={t('contacts.phoneNumber')}
        value={phone}
        onChange={e => { setPhone(e.target.value); setErrors(p => ({ ...p, phone: false })); }}
        className={cn(inputClass, errors.phone && 'border-error-red\/40 bg-error-red\/5')}
      />
      {errors.phone && <p className="text-xs text-error-red -mt-2">{t('contacts.phoneRequired')}</p>}
      <input
        type="email"
        placeholder={t('contacts.email')}
        value={email}
        onChange={e => setEmail(e.target.value)}
        className={inputClass}
      />
    </Modal>
  );
}

export function Contacts() {
  const { t } = useLanguage();

  const { contacts, addContact, updateContact, deleteContact } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [sortOrder, setSortOrder] = useState<'alpha' | 'recent'>('recent');
  const { requestDelete, modal: deleteModal } = useDeleteConfirm();

  const sortedContacts = sortOrder === 'alpha'
    ? [...contacts].sort((a, b) => a.name.localeCompare(b.name))
    : [...contacts];

  const handleDelete = (id: string) => {
    requestDelete(
      t('contacts.deleteContact'),
      t('contacts.deleteContactMsg'),
      () => deleteContact(id)
    );
  };

  return (
    <div className="w-full max-w-5xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-sm bg-primary-brown text-white hover:bg-hover-dark active:scale-[0.98] transition-all"
        >
          <Plus size={18} />
          {t('contacts.addNewContact')}
        </button>

        {/* Sort toggle */}
        <div className="flex rounded-xl border border-gray-200 overflow-hidden shrink-0">
          <button
            onClick={() => setSortOrder('recent')}
            title={t('contacts.sortRecent')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors',
              sortOrder === 'recent'
                ? 'bg-primary-brown text-white'
                : 'bg-white text-text-secondary hover:bg-gray-50'
            )}
          >
            <Clock size={14} />
          </button>
          <button
            onClick={() => setSortOrder('alpha')}
            title={t('contacts.sortAlpha')}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors border-l border-gray-200',
              sortOrder === 'alpha'
                ? 'bg-primary-brown text-white'
                : 'bg-white text-text-secondary hover:bg-gray-50'
            )}
          >
            <ArrowDownAZ size={14} />
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {sortedContacts.map((contact: Contact) => (
          <ContactCard
            key={contact.id}
            contact={contact}
            onDelete={() => handleDelete(contact.id)}
            onEdit={() => setEditingContact(contact)}
          />
        ))}
        {contacts.length === 0 && (
          <div className={cn('text-center py-12 rounded-2xl border border-gray-100 text-gray-400')}>
            <Users size={36} className="mx-auto mb-3 opacity-40" />
            <p className="font-medium">{t('contacts.noContacts')}</p>
            <p className="text-sm mt-1">{t('contacts.addCareTeam')}</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <ContactModal
          onClose={() => setIsModalOpen(false)}
          onSave={data => { addContact(data); setIsModalOpen(false); }}
        />
      )}
      {editingContact && (
        <ContactModal
          initial={editingContact}
          onClose={() => setEditingContact(null)}
          onSave={data => { updateContact(editingContact.id, data); setEditingContact(null); }}
        />
      )}
      {deleteModal}
    </div>
  );
}
